import i18nFactory from '@yandex-int/i18n';
import { difference, intersection, union } from 'lodash';
import moment from 'moment';

import { DateDisplayHelper } from '../../../service/DateDisplayHelper/DateDisplayHelper';
import { LogHelpers } from '../../../service/LogHelpers/LogHelpers';
import { RegionsHelpers } from '../../../service/RegionsHelpers/RegionsHelpers';
import { NetworkFragment, OutputCategory } from '../../../typings/graphql-log';
import {
    ChangedOptions,
    DisplayGroups,
    InlineDisplayGroups,
    LogSourceRowEvent,
    NetworkGroups,
    NullableNumber,
    NullableString,
    RegionsGroups
} from '../../../typings/log';
import { Regions } from '../../../typings/state';
import { filterArrayByNullAndUndefined } from '../../common/common';

import * as keyset from './common.i18n';

const i18n = i18nFactory(keyset);

const trueString = 'true',
    falseString = 'false';

export function prepareValueChangeEvent(category: OutputCategory, event: LogSourceRowEvent): DisplayGroups {
    switch (category) {
        case OutputCategory.CAMPAIGN_START_TIME:
        case OutputCategory.CAMPAIGN_FINISH_TIME:
            return prepareDate(event.oldValue, event.newValue);
        case OutputCategory.CAMPAIGN_EXTENDED_GEOTARGETING:
            return prepareSimpleBoolean(event.newValue);
        default:
            throw Error(`Не поддерживается категория ${category}`);
    }
}

export function prepareStatusData(category: OutputCategory): InlineDisplayGroups {
    let text;
    const res: InlineDisplayGroups = {
        [ChangedOptions.added]: [],
        [ChangedOptions.removed]: [],
        [ChangedOptions.notModified]: [],
        [ChangedOptions.off]: [],
        needExpand: false,
        maxLineLength: 0,
        alwaysNeedExpand: false
    };
    switch (category) {
        case OutputCategory.CAMPAIGN_CREATION:
            text = LogHelpers.getCategoryNameFromEnumString(OutputCategory.CAMPAIGN_CREATION);
            res[ChangedOptions.added] = [text];
            break;
        case OutputCategory.CAMPAIGN_SHOW:
        case OutputCategory.BANNERS_SHOW:
            text = LogHelpers.getCategoryNameFromEnumString(category);
            res[ChangedOptions.added] = [text];
            break;
        default:
            text = LogHelpers.getCategoryNameFromEnumString(category);
            res[ChangedOptions.off] = [text];
            break;
    }
    res.maxLineLength = text.length;
    res.needExpand = LogHelpers.calcNotAlwaysNeedExpand(text.length);
    return res;
}

function prepareDate(before: NullableString = '', after: NullableString = ''): InlineDisplayGroups {
    const afterString =
        after === null
            ? null
            : DateDisplayHelper.getDateText(
                  moment(after)
                      .clone()
                      .utcOffset('+0300'),
                  'DD.MM.YYYY'
              );
    const beforeString =
        before === null
            ? null
            : DateDisplayHelper.getDateText(
                  moment(before)
                      .clone()
                      .utcOffset('+0300'),
                  'DD.MM.YYYY'
              );
    const maxLength = Math.max((afterString || '').length, (beforeString || '').length);
    return {
        [ChangedOptions.added]: filterArrayByNullAndUndefined<string>([afterString]),
        [ChangedOptions.removed]: filterArrayByNullAndUndefined<string>([beforeString]),
        [ChangedOptions.notModified]: [],
        [ChangedOptions.off]: [],
        noInsideComma: true,
        maxLineLength: maxLength,
        alwaysNeedExpand: false,
        needExpand: LogHelpers.calcNotAlwaysNeedExpand(maxLength)
    };
}

function prepareSimpleBoolean(after?: NullableString): InlineDisplayGroups {
    if (!after) {
        throw Error('Некорректное значение временного таргетинга: нет данных');
    }

    const result: InlineDisplayGroups = {
        [ChangedOptions.added]: [],
        [ChangedOptions.removed]: [],
        [ChangedOptions.notModified]: [],
        [ChangedOptions.off]: [],
        needExpand: false,
        maxLineLength: 0,
        alwaysNeedExpand: false
    };
    let text;

    switch (after) {
        case trueString:
            text = LogHelpers.getLocalizedString('on');
            result[ChangedOptions.added] = [text];
            break;
        case falseString:
            text = LogHelpers.getLocalizedString('off');
            result[ChangedOptions.off] = [text];
            break;
        default:
            text = '';
            break;
    }
    result.maxLineLength = text.length;
    result.needExpand = LogHelpers.calcNotAlwaysNeedExpand(text.length);
    return result;
}

export function prepareInlineDisplay(
    before?: Array<NullableString>,
    after?: Array<NullableString>
): InlineDisplayGroups {
    if (!before || !after) {
        throw Error('Некорректные данные в списках: undefined в CampaignListChangeEvent');
    }
    const filteredBefore = filterArrayByNullAndUndefined<string>(before),
        filteredAfter = filterArrayByNullAndUndefined<string>(after),
        allData = union(filteredBefore, filteredAfter),
        added = difference(allData, filteredBefore),
        removed = difference(allData, filteredAfter),
        notModified = intersection(filteredBefore, filteredAfter),
        maxLineLength = allData.join(', ').length;

    return {
        [ChangedOptions.added]: added,
        [ChangedOptions.removed]: removed,
        [ChangedOptions.notModified]: notModified,
        [ChangedOptions.off]: [],
        maxLineLength: maxLineLength,
        alwaysNeedExpand: false,
        needExpand: LogHelpers.calcNotAlwaysNeedExpand(maxLineLength)
    };
}

export function prepareRegionsGroups(
    regionsHash: Regions,
    before?: Array<NullableNumber>,
    after?: Array<NullableNumber>
): RegionsGroups {
    if (!before || !after) {
        throw Error('Некорректное значение географического таргетинга: нет данных');
    }

    const isNotNew = before.length > 0,
        beforeText = isNotNew
            ? RegionsHelpers.getRegionsTexts(regionsHash, filterArrayByNullAndUndefined<number>(before))
            : '',
        afterText = RegionsHelpers.getRegionsTexts(regionsHash, filterArrayByNullAndUndefined<number>(after)),
        maxLineLength = Math.max(beforeText.length, afterText.length);
    return {
        before: beforeText,
        after: afterText,
        alwaysNeedExpand: false,
        needExpand: LogHelpers.calcNotAlwaysNeedExpand(maxLineLength),
        maxLineLength: maxLineLength
    };
}

export function prepareNetwork(before?: NetworkFragment, after?: NetworkFragment): NetworkGroups {
    if (!before || !after) {
        throw Error('Некорректное значение для настроек в сетях: нет данных');
    }

    const maxLineLength = calcNetworkMaxLineLength(after, before);

    return {
        before: before,
        after: after,
        needExpand: LogHelpers.calcNotAlwaysNeedExpand(maxLineLength),
        maxLineLength: maxLineLength,
        alwaysNeedExpand: false
    };
}

function calcNetworkMaxLineLength(after: NetworkFragment, before?: NetworkFragment): number {
    const limitStr = i18n('campaign_context_limit', {
            oldLimitPercent: before ? before.limitPercent : 0,
            newLimitPercent: after.limitPercent
        }),
        limitStrLength =
            !before || before.limitPercent === after.limitPercent
                ? limitStr.length - 3 // 0%+пробел
                : limitStr.length,
        // priceCoefStr = i18n('campaign_context_price_coef', {
        //     oldPriceCoef: before ? before.priceCoef : 0,
        //     newPriceCoef: after.priceCoef
        // }),
        // priceCoefLength =
        //     !before || before.priceCoef === after.priceCoef
        //         ? priceCoefStr.length - 3 // 0%+пробел
        //         : priceCoefStr.length,
        enableCpcLength = i18n('campaign_context_enable_cpc_hold').length;
    return Math.max(limitStrLength, enableCpcLength);
}
